import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import type { ProductDto } from "@gaole/shared";
import { useBodyLock } from "../../hooks/useBodyLock";
import { rm } from "../../lib/format";
import { ProductImage, productImageSrc } from "../ProductCard";
import { ListingTitle } from "../ListingTitle";
import { Badge, statusColor } from "../ui";

export interface RafflePrizePreview {
  product: ProductDto;
  priceForWinner?: number;
  prizeIndex?: number;
  winnerCount?: number;
  status?: string;
}

interface RafflePrizeModalProps {
  prize: RafflePrizePreview | null;
  onClose: () => void;
}

export function RafflePrizeModal({ prize, onClose }: RafflePrizeModalProps) {
  useBodyLock(!!prize);
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => {
    setImageIndex(0);
  }, [prize?.product.id]);

  const gallery =
    prize && (prize.product.displayImages?.length ? prize.product.displayImages : prize.product.images);
  const heroImage = prize ? (gallery?.[imageIndex] ?? productImageSrc(prize.product)) : null;

  return (
    <AnimatePresence
      onExitComplete={() => {
        setImageIndex(0);
      }}
    >
      {prize && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={prize.product.name}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 p-4 backdrop-blur-sm sm:items-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ type: "spring", damping: 28, stiffness: 360 }}
            className="flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-3xl border border-white/10 bg-surface shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white/80 backdrop-blur-sm transition-colors hover:bg-black/55"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            <div className="overflow-y-auto">
              <div className="bg-gradient-to-b from-white/[0.04] to-transparent px-5 pb-4 pt-5">
                <ProductImage
                  src={heroImage}
                  alt={prize.product.name}
                  backgroundColor={prize.product.imageBackgroundColor}
                  className="mx-auto aspect-[5/7] w-full max-w-[240px] rounded-2xl shadow-xl"
                />
                {gallery && gallery.length > 1 && (
                  <div className="mt-3 flex justify-center gap-2">
                    {gallery.map((src, idx) => (
                      <button
                        key={`${src}-${idx}`}
                        type="button"
                        onClick={() => setImageIndex(idx)}
                        className={`overflow-hidden rounded-lg border-2 transition-colors ${
                          imageIndex === idx ? "border-brand-pink" : "border-white/10 opacity-70"
                        }`}
                      >
                        <ProductImage
                          src={src}
                          alt=""
                          backgroundColor={prize.product.imageBackgroundColor}
                          className="h-12 w-12"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-4 px-5 pb-5">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="text-center">
                    {prize.prizeIndex != null && (
                      <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-brand-pink">
                        Prize #{prize.prizeIndex}
                      </p>
                    )}
                    <ListingTitle className="mt-1 font-display text-base font-bold leading-snug text-white">
                      {prize.product.name}
                    </ListingTitle>
                    {prize.product.categoryName && (
                      <p className="mt-1 text-xs text-white/50">{prize.product.categoryName}</p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                      {prize.status && (
                        <Badge color={statusColor(prize.status)}>{prize.status.replace(/_/g, " ")}</Badge>
                      )}
                      {prize.winnerCount != null && (
                        <Badge color="gray">
                          {prize.winnerCount} winner{prize.winnerCount !== 1 ? "s" : ""}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {prize.priceForWinner != null && (
                    <div className="mt-4 border-t border-white/10 pt-4 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-white/50">Winner pays</span>
                        <span className="font-display font-bold text-brand-pink">{rm(prize.priceForWinner)}</span>
                      </div>
                    </div>
                  )}
                </div>

                {prize.product.description && (
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-white/40">About</p>
                    <p className="whitespace-pre-wrap text-xs leading-relaxed text-white/70">{prize.product.description}</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
